import { useCallback, useEffect, useState } from 'react';
import { Flow, flowMeta, flowOrder } from '../content/site';

const STORAGE_KEY = 'solace.flow';

export type FlowKey = Flow | 'undecided';

function flowFromSlug(slug: string | null): Flow | null {
  if (!slug) return null;
  return flowOrder.find((flow) => flowMeta[flow].slug === slug) ?? null;
}

/** URL wins over storage, so a shared link always lands on the intended flow. */
function readInitialFlow(): Flow | null {
  if (typeof window === 'undefined') return null;

  const fromUrl = flowFromSlug(
    new URLSearchParams(window.location.search).get('flow'),
  );
  if (fromUrl) return fromUrl;

  try {
    return flowFromSlug(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * The chosen journey — selling a practice or selling a will bank — which
 * drives the headline, the process steps and the enquiry form. Persisted to
 * the URL so `?flow=will-bank` can be used in adverts and email signatures.
 */
export function useFlow() {
  const [flow, setFlowState] = useState<Flow | null>(readInitialFlow);

  useEffect(() => {
    try {
      if (flow) {
        window.localStorage.setItem(STORAGE_KEY, flowMeta[flow].slug);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* private browsing — the URL still carries the flow */
    }

    const url = new URL(window.location.href);
    if (flow) {
      url.searchParams.set('flow', flowMeta[flow].slug);
    } else {
      url.searchParams.delete('flow');
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [flow]);

  const setFlow = useCallback((next: Flow | null) => setFlowState(next), []);

  return {
    flow,
    setFlow,
    flowKey: (flow ?? 'undecided') as FlowKey,
  };
}
